<script setup lang="ts">
import type { EChartsCoreOption } from 'echarts/core'
import {
  getMarketDetail,
  type MarketDetail,
  type MarketDetailVolmexItem,
  type MarketSummaryCard,
  type TopCrypto,
  type DominanceItem,
  type GasFeeItem,
} from '~/api/market'
import { formatRelativeTime } from '~/utils/appTime'
import { numConvert } from '~/utils/data'
import dayjs from 'dayjs'

const { localePath } = useAppI18n()
const matomo = useMatomo()
const appStore = useAppStore()
const { t } = useAppI18n()
const { isRtl } = useRtl()

definePageMeta({
  name: 'data',
  layout: 'default',
  hideDefaultTopNav: true,
  routeRules: {
    swr: false,
    cache: false,
  },
})

useHead(() => ({
  title: t('84160'),
  meta: [
    {
      name: 'description',
      content: t('84104'),
    },
  ],
}))

const marketTrendDownIcon =
  'https://resources1.interface003.com/web30/2026/07/15/bdea5a71-8c01-41ac-868f-70d2807cc458.svg'
const marketTrendUpIcon =
  'https://resources1.interface003.com/web30/2026/07/15/c438c352-3650-4df7-a6dc-2978b40239e9.svg'
const dropdownArrowIcon =
  'https://resources1.interface003.com/web30/2026/07/14/b9881b88-7b9a-4881-bf9d-7cdb06ff1f9a.png'
const gasInfoIcon = 'https://resources1.interface003.com/web30/2026/07/09/87cf9c3c-68bd-44c7-823f-16803a1a3255.svg'
const volmexInfoIcon = 'https://resources1.interface003.com/web30/2026/07/09/e6efa141-9461-479a-b8b5-d77f5301169c.svg'

// ETF 图表时间范围下拉选项。
const etfRangeOptions = [
  { key: '30d', text: t('82544') }, // 30天
  { key: '1y', text: t('82545') }, // 1年
  { key: 'all', text: t('83794') }, // 全部
]

// ETF 图表当前选中的时间范围。
const selectedEtfRange = ref('30d')
const selectedEtfRangeText = ref(t('82544'))

// ETF 图表时间范围下拉框开关状态。
const isEtfRangeOpen = ref(false)

// ETF 图表时间范围下拉框根元素，用于判断外部点击。
const etfRangeSelectRef = ref<HTMLElement | null>(null)

// ETF 资金流向柱状图容器，交给 ECharts 渲染。
const etfFlowChartRef = ref<HTMLElement | null>(null)

// 市场贪婪指数图表容器。
const greedGaugeRef = ref<HTMLElement | null>(null)

// 市场贪婪指数指针动画进度。
const greedPointerProgress = ref(0)

// 加密季节指数下方箭头动画进度。
const seasonIndicatorProgress = ref(0)
const marketSummaryRef = ref<HTMLElement | null>(null)
const marketSummaryProgress = ref(0)
let hasSummaryPlayed = false
let hasSummaryScheduled = false
let marketSummaryObserver: IntersectionObserver | null = null

// 面包屑。
const breadcrumbItems = [
  {
    label: t('82520'),
    route: '/',
  },
  {
    label: t('82501'),
  },
]

const { data: marketData } = await useAsyncData('data-market-page', async () => {
  const ret = await getMarketDetail({
    module: 'MARKET_DATA',
    lang: appStore.LanguageCode,
  })
  return ret?.data as MarketDetail | undefined
})

const market = computed(() => marketData.value)
const { marketTop5 } = useMarketTop5Sse(() => market.value?.top5)
// console.log('market-22', market)

const GREED_GAUGE_MAX = 100
const GREED_GAUGE_SCORE = market.value?.fearGreed?.currentIndexValue || 0
const GREED_GAUGE_SCORE_RATIO = Math.min(Math.max(GREED_GAUGE_SCORE / GREED_GAUGE_MAX, 0), 1)
const GREED_GAUGE_LABEL = market.value?.fearGreed?.currentIndexLabelText || ''
const GREED_GAUGE_SCALE = 1.2
const GREED_GAUGE_DOT_SIZE = 12 * GREED_GAUGE_SCALE
const GREED_GAUGE_RADIUS = '93.55%'
const GREED_GAUGE_CENTER_Y = '46.7742%'
const GREED_GAUGE_DOT_CENTER_X = 60.4 * GREED_GAUGE_SCALE
const GREED_GAUGE_DOT_CENTER_Y = 5 * GREED_GAUGE_SCALE
const GREED_GAUGE_DOT_START_X = 6 * GREED_GAUGE_SCALE
const GREED_GAUGE_DOT_START_Y = 58 * GREED_GAUGE_SCALE
const GREED_GAUGE_DOT_ARC_CENTER_X = GREED_GAUGE_DOT_CENTER_X
const GREED_GAUGE_DOT_ARC_CENTER_Y = GREED_GAUGE_DOT_START_Y
const GREED_GAUGE_DOT_ARC_RADIUS_X = GREED_GAUGE_DOT_CENTER_X - GREED_GAUGE_DOT_START_X
const GREED_GAUGE_DOT_ARC_RADIUS_Y = GREED_GAUGE_DOT_START_Y - GREED_GAUGE_DOT_CENTER_Y
const GREED_GAUGE_LINE_WIDTH = 7.44 * GREED_GAUGE_SCALE
const GREED_GAUGE_ANIMATION_DELAY = 0
const GREED_GAUGE_ANIMATION_DURATION = 600

const SEASON_SCORE = market.value?.summary?.cryptoSeasonIndex || 0
const SEASON_SCORE_MAX = 100
const SEASON_LEFT_LABEL = 'Bit'
const SEASON_RIGHT_LABEL = 'Alt'
const SEASON_ANIMATION_DELAY = 0
const SEASON_ANIMATION_DURATION = 720
/** 加密季节指数分段条颜色，与 chart-season-bars.svg 保持一致。*/
const SEASON_SEGMENT_COLORS = ['#E2483D', '#FFD5D2', '#CFE1FD', '#1868DB'] as const
/**
 * 加密季节指数分段边界。
 * 0-25 对应首段红色，26-50、51-75、76-100 分别对应其余三段颜色。
 */
const SEASON_SEGMENT_BOUNDARIES = [25.99, 49.99, 74.99] as const

const getTrend = (val: number | null | undefined) => {
  if (val) {
    return val > 0 ? 'up' : val <= 0 ? 'down' : ''
  }
  return 'down'
}

const dealChange = (val: string | null | undefined) => {
  if (typeof val === 'number') {
    return val
  }
  if (typeof val === 'string') {
    return val?.replace('+', '')?.replace('-', '')
  }
}

// 市场概览数据
const marketSummaryCards = computed(() => {
  const _ret: MarketSummaryCard[] = []
  //total Market Cap
  const totalMarketCap = market.value?.summary?.totalMarketCapUsd
  const totalMarketCapRatio = market.value?.summary?.totalMarketCapUsdRatio
  const totalMarketCapValue = totalMarketCap?.value
  const totalMarketCapRatioValue = totalMarketCapRatio?.value
  if (totalMarketCapValue && totalMarketCapRatioValue) {
    _ret.push({
      title: t('82538'), // total Market Cap
      value: totalMarketCapValue,
      change: dealChange(totalMarketCapRatioValue) || '0.00%',
      trend: getTrend(totalMarketCapRatio?.raw),
    })
  }
  //24h Trade Volume
  const totalVolume24hUsd = market.value?.summary?.totalVolume24hUsd
  const totalVolume24hUsdRatio = market.value?.summary?.totalVolume24hUsdRatio
  const totalVolume24hUsdValue = totalVolume24hUsd?.value
  const totalVolume24hUsdRatioValue = totalVolume24hUsdRatio?.value
  if (totalVolume24hUsdValue && totalVolume24hUsdRatioValue) {
    _ret.push({
      title: t('82506'), //24h Trade Volume
      value: totalVolume24hUsdValue,
      change: dealChange(totalVolume24hUsdRatioValue) || '0.00%',
      trend: getTrend(totalVolume24hUsdRatio?.raw),
    })
  }

  const openInterestItems = market.value?.summary?.openInterestItems
  if (openInterestItems && openInterestItems?.length >= 0) {
    // 永续合约
    const perpetualContract = openInterestItems.filter((it) => it.type === 1)
    if (perpetualContract && perpetualContract?.length > 0) {
      _ret.push({
        title: t('84443'), //永续合约 perpetual contract
        value: perpetualContract[0]?.value?.value || '--',
        change: dealChange(perpetualContract[0]?.ratio?.value) || '0.00%',
        trend: getTrend(perpetualContract[0]?.ratio?.raw),
      })
    }
    // 期货
    const futures = openInterestItems.filter((it) => it.type === 2)
    if (futures && futures?.length > 0) {
      _ret.push({
        title: t('84444'), //期货 Futures
        value: futures[0]?.value?.value || '--',
        change: dealChange(futures[0]?.ratio?.value) || '0.00%',
        trend: getTrend(futures[0]?.ratio?.raw),
      })
    }
  }

  return _ret
})

type ParsedSummaryValue = {
  amount: number
  decimals: number
  suffix: string
  isParseable: boolean
}

const parseSummaryCardValue = (value: string): ParsedSummaryValue => {
  const sanitized = value.replaceAll(',', '').trim()
  const matched = /^\$?(\d+(?:\.\d+)?)([A-Za-z]+)?$/.exec(sanitized)
  if (!matched) {
    return { amount: 0, decimals: 0, suffix: '', isParseable: false }
  }
  const amountText = matched[1]
  const suffix = matched[2] ?? ''
  const decimals = amountText.includes('.') ? (amountText.split('.')[1]?.length ?? 0) : 0
  return { amount: Number(amountText), decimals, suffix, isParseable: true }
}

const formatSummaryCardValue = (amount: number, decimals: number, suffix: string) => {
  return `$${amount.toFixed(decimals)}${suffix}`
}

const parsedSummaryCardValues = computed(() => {
  return marketSummaryCards.value.map((card) => parseSummaryCardValue(card.value))
})

const animatedSummaryCardValues = computed(() => {
  return parsedSummaryCardValues.value.map((parsed) => {
    if (!parsed.isParseable) return ''
    return formatSummaryCardValue(parsed.amount * marketSummaryProgress.value, parsed.decimals, parsed.suffix)
  })
})

const marketDataSummaryAnimatedStyle = computed(() => {
  return {
    opacity: `${0.32 + marketSummaryProgress.value * 0.68}`,
    transform: `scale(${0.94 + marketSummaryProgress.value * 0.06})`,
    filter: `blur(${(1 - marketSummaryProgress.value) * 1.4}px)`,
  }
})

const startSummaryAnimation = () => {
  if (hasSummaryPlayed) return
  hasSummaryScheduled = false
  hasSummaryPlayed = true
  marketSummaryProgress.value = 0
  runTween(800, (value) => {
    marketSummaryProgress.value = value
  })
}

const scheduleSummaryAnimation = () => {
  if (hasSummaryPlayed || hasSummaryScheduled) return
  hasSummaryScheduled = true
  window.requestAnimationFrame(() => {
    startSummaryAnimation()
  })
}

const getTips = (val: string) => {
  const tips = market.value?.popup?.sections
  let tip: string | null | undefined = ''
  tips?.forEach((item) => {
    if (item?.key === val) {
      tip = item.content
    }
  })
  return tip
}

// 加密ETF资金流向-总览
const etfFlowSummary = computed(() => {
  const etf = market.value?.cryptoEtf?.find((it) => it.period === selectedEtfRange.value)
  if (etf?.period) {
    return etf.summary
  }
})

const etfFlowChartData = computed(() => {
  const etf = market.value?.cryptoEtf?.find((it) => it.period === selectedEtfRange.value)
  if (etf?.period) {
    return etf.series
  }
})

// ETF 资金流向图表关键尺寸与调色。
const ETF_BITCOIN_COLOR = '#1868db'
const ETF_ETHEREUM_COLOR = '#e2483d'
const ETF_FLOW_BAR_FIRST_STAGE_DURATION = 460
const ETF_FLOW_BAR_SECOND_STAGE_DURATION = 360
const ETF_FLOW_BAR_SECOND_STAGE_DELAY = 290

// 按下标聚合每一天的 Bitcoin / Ethereum ETF 净流入，缺失项填 0；并按时间升序排列，确保 x 轴末端固定为最新日期。
type EtfFlowPoint = {
  timestamp: number
  bitcoin: number
  ethereum: number
}

const etfFlowPoints = computed<EtfFlowPoint[]>(() => {
  const series = etfFlowChartData.value ?? []
  return series
    .filter((point) => typeof point?.timestamp === 'number')
    .toSorted((a, b) => (a.timestamp as number) - (b.timestamp as number))
    .map((point) => {
      const items = point?.items ?? []
      const bitcoin = items.find((it) => it?.name === 'Bitcoin ETFs')?.flowValue ?? 0
      const ethereum = items.find((it) => it?.name === 'Ethereum ETFs')?.flowValue ?? 0
      return {
        timestamp: point!.timestamp as number,
        bitcoin: Number.isFinite(bitcoin) ? bitcoin : 0,
        ethereum: Number.isFinite(ethereum) ? ethereum : 0,
      }
    })
})

// X 轴日期标签。
const etfDateLabels = computed(() => etfFlowPoints.value.map((point) => dayjs(point.timestamp).format('MM-DD')))

// 将金额格式化为 tooltip 需要的 +$xxx / -$xxx 字符串。
const formatEtfFlowAmount = (value: number) => {
  if (!Number.isFinite(value) || value === 0) return '$0'
  const sign = value > 0 ? '+' : '-'
  return sign + '$' + numConvert(Math.abs(value), true)
}

const TOP_CRYPTO_TREND_INTRO_DURATION_MS = 1000
const TOP_CRYPTO_TREND_AREA_MAX_ALPHA = 0.3

// Top 5主流加密货币/Top 5 Cryptos
const topCryptos = computed(() => {
  const _ret: TopCrypto[] = []
  const top5 = marketTop5.value
  if (top5 && top5.length > 0) {
    top5.forEach((it) => {
      const _marketId = it?.marketId
      const symbol = it?.coinName
      const price = it?.price?.value
      const change = it?.change24h?.value
      const icon = it?.coinLogo || ''
      const trend = getTrend(it?.change24h?.raw)
      if (!symbol || !price || !change || !trend) return
      _ret.push({
        marketId: _marketId || '',
        symbol,
        price,
        change,
        trend,
        icon,
        trendSeries:
          it?.trend?.reduce<number[]>((acc, _trend) => {
            if (typeof _trend?.value === 'number') {
              acc.push(_trend.value)
            }
            return acc
          }, []) || [],
      })
    })
  }

  return _ret.length > 5 ? _ret.slice(0, 5) : _ret
})

const DOMINANCE_CHART_COLORS = ['#1769E0', '#E5483F', '#22A06B']
// 市场主导占比 Market Dominance
const marketDominanceItems: DominanceItem[] = []
const marketDominance = market.value?.summary?.marketDominance
marketDominance?.forEach((it, k) => {
  if (k <= 2) {
    marketDominanceItems.push({
      color: DOMINANCE_CHART_COLORS[k] || '',
      icon: it?.icon || 'https://resource.tech002.com/images/projet/item-default-54.svg',
      value: it?.dominancePercentage?.value || '0.0%',
      percent: it?.dominancePercentage?.raw || 0,
    })
  }
})

// Volmex波动率
const volmexVolatilityItem: MarketDetailVolmexItem[] = market.value?.summary?.volmexVolatility || []

// ETH Gas Fee 三档速度数据。
const ethGasFeeItems: GasFeeItem[] = [
  {
    label: t('84575'), // 缓慢
    value: market.value?.summary?.gas?.safe?.display || '--',
    time: '-45s',
  },
  {
    label: t('84576'), // 标准
    value: market.value?.summary?.gas?.propose?.display || '--',
    time: '-45s',
  },
  {
    label: t('84577'), // 快速
    value: market.value?.summary?.gas?.fast?.display || '--',
    time: '-45s',
  },
]

const animatedGreedScore = computed(() => {
  return Math.round(GREED_GAUGE_SCORE * greedPointerProgress.value)
})

const animatedSeasonScore = computed(() => {
  return Math.round(SEASON_SCORE)
})

// 加密季节指数箭头定位样式，随动画进度偏移。
const seasonIndicatorAnimatedStyle = computed(() => {
  const ratio = Math.min(Math.max(SEASON_SCORE / SEASON_SCORE_MAX, 0), 1)
  const animatedRatio = ratio * Math.min(Math.max(seasonIndicatorProgress.value, 0), 1)
  const seasonScore = animatedSeasonScore.value
  const segmentIndex = SEASON_SEGMENT_BOUNDARIES.findIndex((boundary) => seasonScore <= boundary)
  const indicatorColor = SEASON_SEGMENT_COLORS[segmentIndex === -1 ? SEASON_SEGMENT_COLORS.length - 1 : segmentIndex]

  return {
    left: `${animatedRatio * 100}%`,
    opacity: `${seasonIndicatorProgress.value}`,
    'border-bottom': `6px solid ${indicatorColor}`,
  }
})

const { init } = useEcharts()

let greedGaugeChart: ReturnType<typeof init> | null = null
const topCryptoChartRefs = ref<Array<HTMLElement | null>>([])
const topCryptoCharts: Array<ReturnType<typeof init>> = []
const activeRafIds = new Set<number>()
const activeTimeoutIds = new Set<ReturnType<typeof setTimeout>>()
let topCryptoTrendRafId = 0

// 市场主导占比环形图容器与实例。
const dominanceChartRef = ref<HTMLElement | null>(null)
let dominanceChart: ReturnType<typeof init> | null = null

// ETF 资金流向柱状图实例。容器 ref 在前文声明，用于 ECharts init。
let etfFlowChart: ReturnType<typeof init> | null = null

const setTopCryptoChartRef = (el: Element | null, index: number) => {
  topCryptoChartRefs.value[index] = el instanceof HTMLElement ? el : null
}

const clampValue = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

const toHexAlpha = (alpha: number) => {
  return Math.round(clampValue(alpha, 0, 1) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()
}

const buildAnimatedTopCryptoTrendData = (trendData: number[], progress: number) => {
  const clampedProgress = clampValue(progress, 0, 1)
  const dataLength = trendData.length

  if (!dataLength) {
    return []
  }

  if (clampedProgress <= 0) {
    return [trendData[0], ...Array.from<number | null>({ length: dataLength - 1 }).fill(null)]
  }

  if (clampedProgress >= 1) {
    return [...trendData]
  }

  const maxIndex = dataLength - 1
  const position = clampedProgress * maxIndex
  const leftIndex = Math.floor(position)
  const ratio = position - leftIndex
  const animatedData = Array.from<number | null>({ length: dataLength }).fill(null)

  for (let index = 0; index < leftIndex; index += 1) {
    animatedData[index] = trendData[index]
  }

  if (leftIndex >= maxIndex) {
    animatedData[maxIndex] = trendData[maxIndex]
    return animatedData
  }

  const leftValue = trendData[leftIndex]
  const rightValue = trendData[leftIndex + 1]
  animatedData[leftIndex] = leftValue + (rightValue - leftValue) * ratio

  return animatedData
}

const getTopCryptoChartOption = (crypto: TopCrypto, progress = 1): EChartsCoreOption => {
  const trendData = crypto.trendSeries.length > 0 ? crypto.trendSeries : []
  const minValue = Math.min(...trendData)
  const maxValue = Math.max(...trendData)
  const yPadding = (maxValue - minValue) * 0.32 || 1
  const color = crypto.trend === 'up' ? '#1F845A' : '#E2483D'
  const areaAlpha = toHexAlpha(TOP_CRYPTO_TREND_AREA_MAX_ALPHA * clampValue(progress, 0, 1))
  const seriesData = buildAnimatedTopCryptoTrendData(trendData, progress)

  return {
    animation: false,
    grid: {
      top: 1,
      right: 0,
      bottom: 1,
      left: 0,
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trendData.map((_, index) => index),
      show: false,
    },
    yAxis: {
      type: 'value',
      min: minValue - yPadding,
      max: maxValue + yPadding,
      show: false,
      scale: true,
    },
    series: [
      {
        type: 'line',
        data: seriesData,
        animation: false,
        showSymbol: false,
        connectNulls: false,
        smooth: false,
        symbol: 'none',
        lineStyle: {
          width: 1.2,
          color,
          cap: 'round',
          join: 'round',
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: `${color}${areaAlpha}` },
              { offset: 1, color: `${color}00` },
            ],
          },
        },
      },
    ],
    tooltip: {
      show: false,
    },
    silent: true,
  }
}

const renderTopCryptoCharts = (progress = 1) => {
  topCryptos.value.forEach((crypto, index) => {
    const dom = topCryptoChartRefs.value[index]

    if (!dom) {
      return
    }

    const existedChart = topCryptoCharts[index]
    if (existedChart && existedChart.getDom() !== dom) {
      existedChart.dispose()
      topCryptoCharts[index] = null
    }

    if (!topCryptoCharts[index]) {
      topCryptoCharts[index] = init(dom, { lazy: true })
    }

    const chart = topCryptoCharts[index]

    if (!chart) {
      return
    }

    chart.setOption(getTopCryptoChartOption(crypto, progress), {
      notMerge: true,
      lazyUpdate: true,
    })
  })
}

const stopTopCryptoTrendIntroAnimation = () => {
  if (!topCryptoTrendRafId) {
    return
  }

  window.cancelAnimationFrame(topCryptoTrendRafId)
  topCryptoTrendRafId = 0
}

const playTopCryptoTrendIntroAnimation = async () => {
  if (!import.meta.client) {
    return
  }

  await nextTick()
  stopTopCryptoTrendIntroAnimation()
  renderTopCryptoCharts(0)
  resizeTopCryptoCharts()

  const startAt = performance.now()

  const step = (timestamp: number) => {
    const progress = clampValue((timestamp - startAt) / TOP_CRYPTO_TREND_INTRO_DURATION_MS, 0, 1)
    renderTopCryptoCharts(progress)
    resizeTopCryptoCharts()

    if (progress >= 1) {
      topCryptoTrendRafId = 0
      return
    }

    topCryptoTrendRafId = window.requestAnimationFrame(step)
  }

  topCryptoTrendRafId = window.requestAnimationFrame(step)
}

const resizeTopCryptoCharts = () => {
  topCryptoCharts.forEach((chart, index) => {
    const dom = topCryptoChartRefs.value[index]

    if (!chart || !dom) {
      return
    }

    chart.resize({
      width: dom.clientWidth,
      height: dom.clientHeight,
    })
  })
}

watch(topCryptos, async (rows) => {
  if (!import.meta.client) {
    return
  }

  await nextTick()

  for (let index = rows.length; index < topCryptoCharts.length; index += 1) {
    topCryptoCharts[index]?.dispose()
    topCryptoCharts[index] = null
  }

  topCryptoChartRefs.value.length = rows.length
  topCryptoCharts.length = rows.length
  stopTopCryptoTrendIntroAnimation()
  renderTopCryptoCharts()
  resizeTopCryptoCharts()
})

const disposeTopCryptoCharts = () => {
  topCryptoCharts.forEach((chart, index) => {
    chart?.dispose()
    topCryptoCharts[index] = null
  })
  topCryptoCharts.length = 0
}

/**
 * 按首页市场组件的动画进度计算半圆仪表盘圆点位置。
 * @param progress 指针动画进度
 */
const getGreedDotPosition = (progress: number) => {
  const normalized = Math.min(Math.max(progress, 0), 1)
  const angle = normalized * Math.PI

  return {
    x: GREED_GAUGE_DOT_ARC_CENTER_X - GREED_GAUGE_DOT_ARC_RADIUS_X * Math.cos(angle),
    y: GREED_GAUGE_DOT_ARC_CENTER_Y - GREED_GAUGE_DOT_ARC_RADIUS_Y * Math.sin(angle),
  }
}

/**
 * 生成市场贪婪指数仪表盘配置。
 */
const getGreedGaugeOption = (): EChartsCoreOption => {
  const dotPosition = getGreedDotPosition(greedPointerProgress.value * GREED_GAUGE_SCORE_RATIO)

  return {
    animation: false,
    series: [
      {
        type: 'gauge',
        silent: true,
        min: 0,
        max: GREED_GAUGE_MAX,
        startAngle: 180,
        endAngle: 0,
        center: ['50%', GREED_GAUGE_CENTER_Y],
        radius: GREED_GAUGE_RADIUS,
        pointer: {
          show: false,
        },
        anchor: {
          show: false,
        },
        progress: {
          show: true,
          width: GREED_GAUGE_LINE_WIDTH,
          roundCap: true,
          clip: false,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: '#E2483D' },
                { offset: 1, color: '#F8F8F8' },
              ],
            },
          },
        },
        axisLine: {
          roundCap: true,
          lineStyle: {
            width: GREED_GAUGE_LINE_WIDTH,
            color: [[1, 'transparent']],
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          show: false,
        },
        title: {
          show: false,
        },
        data: [{ value: GREED_GAUGE_MAX }],
      },
    ],
    graphic: [
      {
        type: 'circle',
        shape: {
          cx: dotPosition.x,
          cy: dotPosition.y,
          r: GREED_GAUGE_DOT_SIZE / 2,
        },
        style: {
          fill: '#292A2E',
          stroke: '#FFFFFF',
          lineWidth: 2,
        },
        z: 100,
        zlevel: 1,
        silent: true,
      },
    ],
  }
}

const easeOutCubic = (value: number) => {
  return 1 - Math.pow(1 - value, 3)
}

const runTween = (
  duration: number,
  update: (value: number) => void,
  options: {
    delay?: number
    easing?: (value: number) => number
  } = {},
) => {
  const { delay = 0, easing = easeOutCubic } = options

  const launch = () => {
    let startAt = 0
    let rafId = 0

    const step = (timestamp: number) => {
      if (!startAt) {
        startAt = timestamp
      }

      const elapsed = timestamp - startAt
      const progress = Math.min(elapsed / duration, 1)
      update(easing(progress))

      if (progress < 1) {
        rafId = window.requestAnimationFrame(step)
        activeRafIds.add(rafId)
      } else {
        activeRafIds.delete(rafId)
      }
    }

    rafId = window.requestAnimationFrame(step)
    activeRafIds.add(rafId)
  }

  if (!delay) {
    launch()
    return
  }

  const timeoutId = window.setTimeout(() => {
    activeTimeoutIds.delete(timeoutId)
    launch()
  }, delay)

  activeTimeoutIds.add(timeoutId)
}

/**
 * 清理市场贪婪指数入场动画。
 */
const clearGreedGaugeAnimation = () => {
  activeRafIds.forEach((rafId) => {
    window.cancelAnimationFrame(rafId)
  })
  activeRafIds.clear()

  activeTimeoutIds.forEach((timeoutId) => {
    clearTimeout(timeoutId)
  })
  activeTimeoutIds.clear()
}

/**
 * 启动市场贪婪指数入场动画。
 */
const startGreedGaugeAnimation = () => {
  greedPointerProgress.value = 0
  seasonIndicatorProgress.value = 0
  renderGreedGauge()

  runTween(
    GREED_GAUGE_ANIMATION_DURATION,
    (value) => {
      greedPointerProgress.value = value
      renderGreedGauge()
    },
    {
      delay: GREED_GAUGE_ANIMATION_DELAY,
    },
  )

  runTween(
    SEASON_ANIMATION_DURATION,
    (value) => {
      seasonIndicatorProgress.value = value
    },
    {
      delay: SEASON_ANIMATION_DELAY,
    },
  )
}

/**
 * 渲染市场贪婪指数仪表盘。
 */
const renderGreedGauge = () => {
  if (!greedGaugeChart) {
    return
  }

  greedGaugeChart.setOption(getGreedGaugeOption(), {
    notMerge: true,
    lazyUpdate: true,
  })
}

/**
 * 初始化市场贪婪指数仪表盘。
 */
const initGreedGauge = () => {
  if (!greedGaugeRef.value) {
    return
  }

  greedGaugeChart = init(greedGaugeRef.value, { lazy: true })
  renderGreedGauge()
}

/**
 * 释放并重新初始化市场贪婪指数仪表盘，用于语言切换（含 RTL/LTR 变化）后的重绘。
 */
const reinitGreedGauge = () => {
  if (!import.meta.client) {
    return
  }
  clearGreedGaugeAnimation()
  if (greedGaugeChart) {
    greedGaugeChart.dispose()
    greedGaugeChart = null
  }
  nextTick(() => {
    initGreedGauge()
    if (greedGaugeChart && greedGaugeRef.value) {
      greedGaugeChart.resize({
        width: greedGaugeRef.value.clientWidth,
        height: greedGaugeRef.value.clientHeight,
      })
    }
    startGreedGaugeAnimation()
  })
}

/**
 * 生成市场主导占比环形图配置，arc 大小根据各项 percent 自动分配。
 */
const getDominanceChartOption = (): EChartsCoreOption => {
  return {
    tooltip: { show: false },
    series: [
      {
        type: 'pie',
        radius: ['81%', '100%'],
        center: ['50%', '50%'],
        silent: true,
        avoidLabelOverlap: false,
        startAngle: 90,
        clockwise: false,
        label: { show: false },
        labelLine: { show: false },
        emphasis: { scale: false },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
        },
        data: marketDominanceItems.map((item) => ({
          value: item.percent,
          name: item.value,
          itemStyle: { color: item.color },
        })),
      },
    ],
  }
}

/**
 * 渲染市场主导占比环形图。
 */
const renderDominanceChart = () => {
  if (!dominanceChart) {
    return
  }

  dominanceChart.setOption(getDominanceChartOption(), {
    notMerge: true,
    lazyUpdate: true,
  })
}

/**
 * 初始化市场主导占比环形图。
 */
const initDominanceChart = () => {
  if (!dominanceChartRef.value || marketDominanceItems.length === 0) {
    return
  }

  dominanceChart = init(dominanceChartRef.value, { lazy: true })
  renderDominanceChart()
}

/**
 * 生成 ETF 资金流向柱状图配置：以 0 为基准的堆叠柱状图，蓝色为 Bitcoin ETFs、红色为 Ethereum ETFs。
 */
const getEtfFlowChartOption = (): EChartsCoreOption => {
  const points = etfFlowPoints.value
  const labels = etfDateLabels.value
  const bitcoinValues = points.map((p) => p.bitcoin)
  const ethereumValues = points.map((p) => p.ethereum)
  // 1 年数据点密集，固定为 10px 以提升柱体辨识度；
  // 其它范围继续由 ECharts 自适应宽度。
  const etfBarWidth = selectedEtfRange.value === '1y' ? 10 : undefined

  // 让 y 轴以 0 为中心上下对称：取每根柱子正/负堆叠总和的最大绝对值，
  // 再上下各留 15% 的空白，避免最长的柱子顶到边线。
  const symmetricExtent = points.reduce((max, point) => {
    const positiveStack = Math.max(point.bitcoin, 0) + Math.max(point.ethereum, 0)
    const negativeStack = Math.min(point.bitcoin, 0) + Math.min(point.ethereum, 0)
    return Math.max(max, positiveStack, Math.abs(negativeStack))
  }, 0)
  const paddedExtent = (symmetricExtent > 0 ? symmetricExtent : 1) * 1.15
  // 向上取整到"1 位有效数字"的整数刻度，如 733M -> 800M、12M -> 20M、550M -> 600M。
  const magnitude = Math.pow(10, Math.floor(Math.log10(paddedExtent)))
  const axisBound = Math.ceil(paddedExtent / magnitude) * magnitude

  return {
    // 保留柱状图入场动效，但避免按 dataIndex 依次延迟导致的“从左到右加载”。
    animation: true,
    animationDuration: ETF_FLOW_BAR_FIRST_STAGE_DURATION,
    animationDurationUpdate: 0,
    animationEasing: 'cubicOut',
    animationDelay: () => 0,
    animationDelayUpdate: () => 0,
    grid: {
      top: 12,
      right: isRtl.value ? 48 : 12,
      bottom: 24,
      left: isRtl.value ? 12 : 48,
      containLabel: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#7d818a',
          type: 'dashed',
          width: 0.5,
        },
      },
      backgroundColor: 'rgba(5, 12, 31, 0.46)',
      borderWidth: 0,
      padding: [8, 12],
      textStyle: {
        color: '#fff',

        fontSize: 12,
      },
      formatter: (params: unknown) => {
        const list = Array.isArray(params) ? params : [params]
        const first = list[0] as { dataIndex?: number } | undefined
        const idx = typeof first?.dataIndex === 'number' ? first.dataIndex : -1
        const point = points[idx]
        if (!point) return ''
        const total = point.bitcoin + point.ethereum
        const time = dayjs(point.timestamp).format('YYYY-MM-DD HH:mm')
        const rows = [
          `<div style="font-size:13px;font-weight:500;line-height:20px;margin-bottom:2px;">${time}</div>`,
          // `<div style="line-height:18px;">总流向：${formatEtfFlowAmount(total)}</div>`,
          `<div style="line-height:18px;display:flex;align-items:center;gap:4px;"><span style="display:inline-block;width:4px;height:4px;border-radius:50%;background:${ETF_BITCOIN_COLOR};"></span>Bitcoin ETFs：${formatEtfFlowAmount(point.bitcoin)}</div>`,
          `<div style="line-height:18px;display:flex;align-items:center;gap:4px;"><span style="display:inline-block;width:4px;height:4px;border-radius:50%;background:${ETF_ETHEREUM_COLOR};"></span>Ethereum ETFs：${formatEtfFlowAmount(point.ethereum)}</div>`,
        ]
        return rows.join('')
      },
    },
    xAxis: {
      type: 'category',
      data: labels,
      inverse: isRtl.value,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#7d818a',

        fontSize: 10,
        margin: 8,
        // 每间隔一个数据点展示一个标签，避免一年维度的日期标签过于密集。
        interval: 1,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      position: isRtl.value ? 'right' : 'left',
      min: -axisBound,
      max: axisBound,
      interval: axisBound / 2,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: {
          color: 'rgba(11, 18, 40, 0.08)',
          type: 'dashed',
        },
      },
      axisLabel: {
        color: '#7d818a',

        fontSize: 10,
        formatter: (value: number) => {
          if (value === 0) return '0'
          const abs = Math.abs(value)
          if (abs >= 1e8) return `${(abs / 1e9).toFixed(1)}B`
          if (abs >= 1e5) return `${(abs / 1e6).toFixed(1)}M`
          if (abs >= 1e3) return `${Math.round(abs / 1e3)}K`
          return `${abs}`
        },
      },
    },
    series: [
      {
        name: 'Bitcoin ETFs',
        type: 'bar',
        stack: 'etf',
        barWidth: etfBarWidth,
        barMaxWidth: 16,
        animationType: 'expansion',
        animationTypeUpdate: 'expansion',
        animationDuration: ETF_FLOW_BAR_FIRST_STAGE_DURATION,
        animationDelay: () => 0,
        animationDelayUpdate: () => 0,
        itemStyle: { color: ETF_BITCOIN_COLOR },
        emphasis: { disabled: true },
        data: bitcoinValues,
      },
      {
        name: 'Ethereum ETFs',
        type: 'bar',
        stack: 'etf',
        barWidth: etfBarWidth,
        barMaxWidth: 16,
        animationType: 'expansion',
        animationTypeUpdate: 'expansion',
        animationDuration: ETF_FLOW_BAR_SECOND_STAGE_DURATION,
        animationDelay: () => ETF_FLOW_BAR_SECOND_STAGE_DELAY,
        animationDelayUpdate: () => 0,
        itemStyle: { color: ETF_ETHEREUM_COLOR },
        emphasis: { disabled: true },
        data: ethereumValues,
      },
    ],
  }
}

/**
 * 渲染 ETF 资金流向柱状图。
 */
const renderEtfFlowChart = () => {
  if (!etfFlowChart) {
    return
  }

  etfFlowChart.setOption(getEtfFlowChartOption(), {
    notMerge: true,
    lazyUpdate: true,
  })
}

/**
 * 初始化 ETF 资金流向柱状图。
 */
const initEtfFlowChart = () => {
  if (!etfFlowChartRef.value) {
    return
  }

  etfFlowChart = init(etfFlowChartRef.value, { lazy: true })
  renderEtfFlowChart()
}

/**
 * 释放并重新初始化 ETF 资金流向柱状图，用于语言切换（含 RTL/LTR 变化）后的重绘。
 */
const reinitEtfFlowChart = () => {
  if (!import.meta.client) {
    return
  }
  if (etfFlowChart) {
    etfFlowChart.dispose()
    etfFlowChart = null
  }
  nextTick(() => {
    initEtfFlowChart()
    if (etfFlowChart && etfFlowChartRef.value) {
      etfFlowChart.resize({
        width: etfFlowChartRef.value.clientWidth,
        height: etfFlowChartRef.value.clientHeight,
      })
    }
  })
}

/**
 * 监听页面尺寸变化并重绘仪表盘。
 */
const onResize = () => {
  if (greedGaugeChart) {
    greedGaugeChart.resize()
    renderGreedGauge()
  }

  if (dominanceChart) {
    dominanceChart.resize()
  }

  if (etfFlowChart) {
    etfFlowChart.resize()
  }

  resizeTopCryptoCharts()
}

/**
 * 切换 ETF 图表时间范围下拉框。
 */
const toggleEtfRangeDropdown = () => {
  isEtfRangeOpen.value = !isEtfRangeOpen.value
}

/**
 * 选择 ETF 图表时间范围，并关闭下拉框。
 * @param option 时间范围文案
 */
const selectEtfRange = (option: string) => {
  selectedEtfRange.value = option
  const mathoption = etfRangeOptions.filter((it) => it.key === option)
  selectedEtfRangeText.value = mathoption?.length > 0 ? mathoption[0]?.text || '' : ''
  isEtfRangeOpen.value = false
}

// 切换时间范围后重绘 ETF 资金流向图。
watch(selectedEtfRange, () => {
  renderEtfFlowChart()
})

// 语言切换（LTR/RTL 变化）后销毁并重建图表，确保坐标轴/仪表盘方向按新方向渲染。
watch(isRtl, () => {
  reinitGreedGauge()
  reinitEtfFlowChart()
})

/**
 * 点击页面其它区域时关闭 ETF 图表时间范围下拉框。
 * @param event 鼠标点击事件
 */
const closeEtfRangeOnOutsideClick = (event: MouseEvent) => {
  const target = event.target

  if (target instanceof Node && !etfRangeSelectRef.value?.contains(target)) {
    isEtfRangeOpen.value = false
  }
}

const getMarketLink = (item: TopCrypto) => {
  if (item.marketId) {
    return localePath({
      path: localePath('/market'),
      query: { code: item.marketId },
    })
  }
}

const goMarketPage = (item: TopCrypto) => {
  if (item.marketId) {
    matomo.trackEvent('home_market', {
      af_content: item.symbol,
      af_code: item.marketId,
    })
  }
}

onMounted(() => {
  initGreedGauge()
  initDominanceChart()
  initEtfFlowChart()
  void playTopCryptoTrendIntroAnimation()
  startGreedGaugeAnimation()
  // Start market summary cards intro animation immediately on mount.
  scheduleSummaryAnimation()
  window.addEventListener('resize', onResize)
  document.addEventListener('click', closeEtfRangeOnOutsideClick)
})

onBeforeUnmount(() => {
  clearGreedGaugeAnimation()
  marketSummaryObserver?.disconnect()
  marketSummaryObserver = null
  stopTopCryptoTrendIntroAnimation()
  window.removeEventListener('resize', onResize)
  document.removeEventListener('click', closeEtfRangeOnOutsideClick)
  disposeTopCryptoCharts()
  greedGaugeChart?.dispose()
  greedGaugeChart = null
  dominanceChart?.dispose()
  dominanceChart = null
  etfFlowChart?.dispose()
  etfFlowChart = null
})
</script>

<template>
  <HeaderWikiBitPcTopNav :always-expand-search="true" :force-solid-background="true" />
  <main class="market-data-page">
    <div class="market-data-breadcrumb">
      <CommonBreadcrumb :items="breadcrumbItems" />
    </div>

    <section class="market-data-heading" aria-label="Market Data">
      <div class="market-data-heading__title-wrap">
        <h1 class="market-data-heading__title">{{ t('82501') }}</h1>
        <p class="market-data-heading__source">（ {{ t('83579') }} ）</p>
      </div>
      <p class="market-data-heading__updated">
        {{ $t('82548') }}：{{ formatRelativeTime(market?.summary?.updateTime, appStore.LanguageCode) || '--' }}
      </p>
    </section>

    <section ref="marketSummaryRef" class="market-data-summary" aria-label="Market Data Summary">
      <article
        v-for="(card, index) in marketSummaryCards"
        :key="card.title"
        class="market-data-summary__card hover:shadow-[0_12px_32px_rgba(8,19,62,0.08)]"
      >
        <p class="market-data-summary__title">{{ card.title }}</p>
        <div class="market-data-summary__value-wrap">
          <p class="market-data-summary__value" :style="marketDataSummaryAnimatedStyle">
            {{ animatedSummaryCardValues[index] || card.value }}
          </p>
          <div class="market-data-summary__change-wrap">
            <p class="market-data-summary__change" :class="`market-data-summary__change--${card.trend}`">
              {{ card.change }}
            </p>
            <CommonImage
              class="market-data-summary__trend-icon"
              :src="card.trend === 'up' ? marketTrendUpIcon : marketTrendDownIcon"
              alt=""
              width="14"
              height="14"
              aria-hidden="true"
            />
          </div>
        </div>
      </article>
    </section>

    <section class="market-data-insights" aria-label="Crypto ETF Fund Flow and Top Cryptos">
      <div class="market-data-insights__chart-card">
        <div class="market-data-insights__header">
          <h2 class="market-data-insights__title">{{ t('82543') }}</h2>
          <div ref="etfRangeSelectRef" class="market-data-insights__range-select">
            <button
              class="market-data-insights__range-button"
              type="button"
              :aria-expanded="isEtfRangeOpen"
              aria-haspopup="listbox"
              @click.stop="toggleEtfRangeDropdown"
            >
              <span class="market-data-insights__range-text">{{ selectedEtfRangeText }}</span>
              <span
                class="market-data-insights__range-icon"
                :class="{ 'market-data-insights__range-icon--open': isEtfRangeOpen }"
                aria-hidden="true"
              >
                <CommonImage :src="dropdownArrowIcon" alt="" width="14" height="14" />
              </span>
            </button>
            <div v-if="isEtfRangeOpen" class="market-data-insights__range-menu" role="listbox">
              <button
                v-for="option in etfRangeOptions"
                :key="option"
                class="market-data-insights__range-option"
                :class="{ 'market-data-insights__range-option--active': option.key === selectedEtfRange }"
                type="button"
                role="option"
                :aria-selected="option.key === selectedEtfRange"
                @click="selectEtfRange(option.key)"
              >
                {{ option.text }}
              </button>
            </div>
          </div>
        </div>

        <div :dir="isRtl ? 'rtl' : 'ltr'" class="market-data-insights__legend" tsv="dikh7">
          <div v-for="(item, k) in etfFlowSummary" :key="k" class="market-data-insights__legend-group">
            <p dir="ltr" class="market-data-insights__legend-value">{{ item.totalNetFlow }}</p>
            <div class="market-data-insights__legend-label">
              <span
                :class="[
                  k === 0
                    ? 'market-data-insights__dot market-data-insights__dot--blue'
                    : 'market-data-insights__dot market-data-insights__dot--red',
                ]"
              ></span>
              <span>{{ item.symbol }}</span>
            </div>
          </div>
        </div>

        <div class="market-data-insights__chart">
          <div
            id="etfchart"
            ref="etfFlowChartRef"
            class="market-data-insights__echart"
            :dir="isRtl ? 'rtl' : 'ltr'"
            role="img"
            aria-label="Crypto ETF fund flow chart"
          ></div>
        </div>
      </div>

      <aside class="market-data-insights__top-card" aria-label="Top 5 Cryptos">
        <h2 class="market-data-insights__title !px-[16px]">{{ t('84578') }}</h2>
        <div class="market-data-insights__crypto-list">
          <NuxtLink
            v-for="(crypto, index) in topCryptos"
            :key="crypto.symbol"
            :to="getMarketLink(crypto)"
            @click="goMarketPage(crypto)"
            class="market-data-insights__crypto-item"
            :class="[crypto?.marketId ? 'cursor-pointer hover:bg-[#f7f8fa]' : '']"
            target="_blank"
          >
            <div class="market-data-insights__crypto-info">
              <div class="market-data-insights__crypto-name">
                <CommonImage
                  v-if="crypto.icon"
                  :src="crypto.icon"
                  alt=""
                  width="16"
                  height="16"
                  aria-hidden="true"
                  class="rounded-[50%]"
                />
                <span>{{ crypto.symbol }}</span>
              </div>
              <div class="market-data-insights__crypto-price-row">
                <p class="market-data-insights__crypto-price">{{ crypto.price }}</p>
                <div
                  class="market-data-insights__crypto-change"
                  :class="`market-data-insights__crypto-change--${crypto.trend}`"
                >
                  <span>{{ crypto.change }}</span>
                  <CommonImage
                    :src="crypto.trend === 'up' ? marketTrendUpIcon : marketTrendDownIcon"
                    alt=""
                    width="10"
                    height="10"
                    aria-hidden="true"
                    :class="{ 'market-data-insights__crypto-trend-icon--up': crypto.trend === 'up' }"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="crypto?.trendSeries?.length > 0"
              :ref="(el: any) => setTopCryptoChartRef(el, index)"
              class="market-data-insights__sparkline"
              aria-hidden="true"
            ></div>
            <div v-else class="market-data-insights__sparkline">
              <div class="flex w-full min-w-[80px] flex-col items-end justify-center space-y-[4px] px-[2px]">
                <p class="font-[Outfit] text-[11px] leading-[16px] text-[#8C8F97]">{{ t('84579') }}</p>
                <CommonImage
                  class="w-[78px]"
                  src="https://resources1.interface003.com/web30/2026/07/02/9f02d4c0-f287-4575-8524-62bbd82d948d.png"
                />
              </div>
            </div>
          </NuxtLink>
        </div>
      </aside>
    </section>

    <section class="market-data-indicators" aria-label="Market Indicators">
      <div class="market-data-indicators__group">
        <article class="market-data-indicators__card market-data-indicators__card--small">
          <h2 class="market-data-indicators__title">{{ t('82539') }}</h2>
          <div class="market-data-indicators__greed-chart">
            <div
              id="greedgaudechart"
              ref="greedGaugeRef"
              class="market-data-indicators__gauge"
              :dir="isRtl ? 'rtl' : 'ltr'"
              aria-hidden="true"
            ></div>
            <div class="market-data-indicators__greed-info">
              <p class="market-data-indicators__greed-value">{{ animatedGreedScore }}</p>
              <p class="market-data-indicators__greed-label">{{ GREED_GAUGE_LABEL }}</p>
            </div>
          </div>
        </article>

        <article class="market-data-indicators__card market-data-indicators__card--small">
          <h2 class="market-data-indicators__title">{{ t('82540') }}</h2>
          <div class="market-data-indicators__season">
            <div class="market-data-indicators__season-labels">
              <span>{{ SEASON_LEFT_LABEL }}</span>
              <span>{{ SEASON_RIGHT_LABEL }}</span>
            </div>
            <div
              id="seasonChart"
              class="market-data-indicators__season-track"
              :dir="isRtl ? 'rtl' : 'ltr'"
              aria-hidden="true"
            >
              <div class="market-data-indicators__season-bar-wrap">
                <span class="market-data-indicators__season-bars" aria-hidden="true" />
              </div>
              <span
                id="mk-seasonIndicator-down"
                class="market-data-indicators__season-indicator"
                :style="seasonIndicatorAnimatedStyle"
              ></span>
            </div>
            <div class="market-data-indicators__season-value-wrap">
              <span class="market-data-indicators__season-value">{{ animatedSeasonScore }}</span>
              <span class="market-data-indicators__season-max">/{{ SEASON_SCORE_MAX }}</span>
            </div>
          </div>
        </article>

        <article class="market-data-indicators__card market-data-indicators__card--small">
          <h2 class="market-data-indicators__title">{{ t('82541') }}</h2>
          <div class="market-data-indicators__dominance">
            <div
              ref="dominanceChartRef"
              class="market-data-indicators__dominance-chart"
              role="img"
              aria-label="Market dominance chart"
            ></div>
            <div class="market-data-indicators__dominance-list">
              <div
                v-for="item in marketDominanceItems"
                :key="item.value"
                class="market-data-indicators__dominance-item"
              >
                <span
                  class="market-data-indicators__dominance-dot"
                  :style="{ backgroundColor: item.color }"
                  aria-hidden="true"
                ></span>
                <div class="market-data-indicators__dominance-value">
                  <CommonImage :src="item.icon" alt="" width="16" height="16" aria-hidden="true" />
                  <strong>{{ item.value }}</strong>
                </div>
              </div>
            </div>
          </div>
        </article>

        <article class="market-data-indicators__card market-data-indicators__card--small">
          <div class="market-data-indicators__title-row">
            <h2 class="market-data-indicators__title">{{ t('82542') }}</h2>
            <span class="market-data-indicators__info">
              <CommonImage :src="volmexInfoIcon" alt="" width="16" height="16" aria-hidden="true" />
              <span class="market-data-indicators__tooltip" role="tooltip">
                {{ getTips('VOLMEX_VOLATILITY') }}
              </span>
            </span>
          </div>
          <div class="market-data-indicators__volmex">
            <div v-for="item in volmexVolatilityItem" class="market-data-indicators__volmex-row">
              <span>{{ item?.symbol }}</span>
              <strong>{{ item?.value }}</strong>
            </div>
            <div class="flex w-full items-center justify-end">
              <CommonImage
                class="h-[20px] w-[104px]"
                src="https://resources1.interface003.com/web30/2026/07/08/e89d0137-03d7-4f23-b2e7-53df909f99dc.png"
                alt=""
              />
            </div>
          </div>
        </article>
      </div>

      <article class="market-data-indicators__card market-data-indicators__card--gas">
        <div class="market-data-indicators__title-row">
          <h2 class="market-data-indicators__title">ETH Gas Fee</h2>
          <span class="market-data-indicators__info">
            <CommonImage :src="gasInfoIcon" alt="" width="16" height="16" aria-hidden="true" />
            <span class="market-data-indicators__tooltip" role="tooltip">
              {{ getTips('ETH_GAS') }}
            </span>
          </span>
        </div>
        <div class="market-data-indicators__gas-list">
          <div v-for="item in ethGasFeeItems" :key="item.label" class="market-data-indicators__gas-item">
            <p class="market-data-indicators__gas-label">{{ item.label }}</p>
            <p class="market-data-indicators__gas-value">{{ item.value }}</p>
            <p
              dir="ltr"
              class="market-data-indicators__gas-time"
              :style="{ 'justify-content': isRtl ? 'flex-end' : 'flex-start' }"
            >
              {{ item.time }}
            </p>
          </div>
        </div>
      </article>
    </section>
  </main>
</template>

<style scoped lang="less">
.market-data-page {
  --market-data-container-width: 1200px;

  box-sizing: border-box;
  min-height: 780px;
  padding-top: 70px;
  padding-bottom: 70px;
  overflow-x: clip;
  background: #f8f8f8;
}

.market-data-breadcrumb {
  width: var(--market-data-container-width);
  margin: 0 auto;
}

.market-data-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: var(--market-data-container-width);
  height: 32px;
  margin: 0 auto;
  white-space: nowrap;
  word-break: break-word;

  &__title-wrap {
    display: flex;
    align-items: flex-end;
    flex-shrink: 0;
    gap: 2px;
    height: 32px;
  }

  &__title {
    flex-shrink: 0;
    margin: 0;
    color: #292a2e;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    letter-spacing: 0;
  }

  &__source,
  &__updated {
    flex-shrink: 0;
    margin: 0;
    color: #8c8f97;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0;
  }

  &__source {
    display: flex;
    align-items: center;
    height: 22px;
  }

  &__updated {
    display: flex;
    align-items: center;
    height: 22px;
  }
}

.market-data-summary {
  display: flex;
  align-items: center;
  gap: 20px;
  width: var(--market-data-container-width);
  height: 138px;
  margin: 20px auto 0;

  &__card {
    box-sizing: border-box;
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    height: 138px;
    min-width: 0;
    padding: 20px 16px;
    overflow: hidden;
    background: #fff;
    border-radius: 8px;
    background: #fff;
    transform: translateY(0) scale(1);
    transform-origin: center center;
    transition:
      transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
    will-change: transform, box-shadow;
  }

  &__title {
    width: 100%;
    height: 22px;
    margin: 0;
    color: #505258;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0;
    word-break: break-word;
  }

  &__value-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    height: 68px;
  }

  &__value {
    width: 100%;
    height: 38px;
    margin: 0;
    color: #292a2e;
    font-size: 32px;
    font-weight: 700;
    line-height: 38px;
    letter-spacing: 0;
    word-break: break-word;
  }

  &__change-wrap {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 22px;
  }

  &__change {
    flex-shrink: 0;
    height: 22px;
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0;
    white-space: nowrap;
    word-break: break-word;
  }

  &__change--down {
    color: #e2483d;
  }

  &__change--up {
    color: #1f845a;
  }

  &__trend-icon {
    display: block;
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    animation: market-data-trend-down 1.1s ease-in-out infinite;
  }

  &__trend-icon--up {
    animation-name: market-data-trend-up;
  }
}

.market-data-insights {
  display: flex;
  align-items: center;
  gap: 20px;
  width: var(--market-data-container-width);
  height: 400px;
  margin: 20px auto 0;

  &__chart-card {
    box-sizing: border-box;
    height: 400px;
    background: #fff;
    border-radius: 8px;
  }

  &__top-card {
    box-sizing: border-box;
    height: 400px;
    background: #fff;
    border-radius: 8px;
  }

  &__chart-card {
    position: relative;
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
    padding: 16px 0px;
    overflow: hidden;
    opacity: 0.96;
  }

  &__top-card {
    flex-shrink: 0;
    width: 285px;
    padding: 10px 0px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 30px;
    padding: 0px 16px;
  }

  &__title {
    margin: 0;
    color: #292a2e;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0;
    white-space: nowrap;
    word-break: break-word;
    padding: 0px;
  }

  &__range-select {
    position: relative;
    justify-content: flex-end;
    display: flex;
    z-index: 10;
    flex-shrink: 0;

    // 保留按钮与菜单之间的 hover 连续区域，不改变可见布局。
    &::after {
      position: absolute;
      top: 30px;
      right: 0;
      z-index: 19;
      width: 92px;
      height: 4px;
      content: '';
    }
  }

  &__range-button {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 30px;
    padding: 4px 12px;
    cursor: pointer;
    background: transparent;
    box-shadow: 0 0 0 0.5px rgba(11, 18, 40, 0.14);
    border-radius: 2px;

    &:hover {
      box-shadow: 0 0 0 0.5px #112d73;
    }
    &:active {
      box-shadow: 0 0 0 0.5px #0a1c50;
    }
    &:focus:not(:active) {
      border-color: #112d73;
      outline: none;
      border: 1px solid #112d73;
      box-shadow: 0 0 0 2px #cfe1fd;
    }

    .market-data-insights__range-text {
      color: #292a2e;
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      white-space: nowrap;
    }
  }

  &__range-icon {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    overflow: hidden;

    img {
      display: flex;
      width: 14px;
      height: 14px;
    }
  }

  &__range-icon--open {
    transform: rotate(180deg);
  }

  &__range-menu {
    position: absolute;
    top: 34px;
    right: 0;
    z-index: 20;
    box-sizing: border-box;
    width: 92px;
    padding: 4px 0;
    overflow: hidden;
    background: #fff;
    border: 0.5px solid rgba(11, 18, 40, 0.14);
    border-radius: 2px;
    box-shadow:
      0 0 1px 0 rgba(30, 31, 33, 0.16),
      0 8px 16px 0 rgba(30, 31, 33, 0.12);
  }

  &__range-option {
    box-sizing: border-box;
    width: 100%;
    height: 28px;
    padding: 3px 12px;
    color: #505258;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    text-align: center;
    cursor: pointer;
    background: transparent;
    border: 0;
  }

  &__range-option:hover,
  &__range-option--active {
    color: #292a2e;
    background: #f8f8f8;
  }

  &__legend {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    padding: 0px 16px;
  }

  &__legend-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  &__legend-value {
    width: 100%;
    margin: 0;
    color: #292a2e;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0;
  }

  &__legend-label {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 18px;
    color: #505258;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    white-space: nowrap;
  }

  &__dot {
    display: block;
    flex-shrink: 0;
    width: 4px;
    height: 4px;
    border-radius: 100px;
  }

  &__dot--blue {
    background: #1868db;
  }

  &__dot--red {
    background: #e2483d;
  }

  &__chart {
    position: relative;
    align-self: stretch;
    width: 100%;
    height: 266px;
  }

  &__echart {
    width: 100%;
    height: 100%;
  }

  &__crypto-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    margin-top: 10px;
  }

  &__crypto-item {
    display: flex;
    align-items: center;
    width: calc(100% - 16px);
    height: 70px;
    padding: 10px 8px;
    margin: 0px 8px;
    border-radius: 4px;
  }

  &__crypto-info {
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    height: 50px;
  }

  &__crypto-name {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    height: 22px;

    img {
      display: block;
      width: 16px;
      height: 16px;
      object-fit: cover;
    }

    span {
      color: #292a2e;
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      white-space: nowrap;
      word-break: break-word;
    }
  }

  &__crypto-price-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
    height: 24px;
  }

  &__crypto-price {
    margin: 0;
    color: #292a2e;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    white-space: nowrap;
    word-break: break-word;
  }

  &__crypto-change {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 18px;

    span {
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
      white-space: nowrap;
    }

    img {
      display: block;
      width: 10px;
      height: 10px;
      animation: market-data-trend-down 1.1s ease-in-out infinite;
    }
  }

  &__crypto-change--down {
    color: #e2483d;
  }

  &__crypto-change--up {
    color: #1f845a;
  }

  &__crypto-trend-icon--up {
    animation-name: market-data-trend-up;
  }

  &__crypto-change img&__crypto-trend-icon--up {
    animation-name: market-data-trend-up;
  }

  &__sparkline {
    display: block;
    flex-shrink: 0;
    width: 78px;
    height: 27.5px;
  }
}

.market-data-indicators {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  width: var(--market-data-container-width);
  height: 150px;
  margin: 20px auto 0;

  &__group {
    display: flex;
    flex-shrink: 0;
    gap: 20px;
    width: 895px;
    height: 150px;
  }

  &__card {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    height: 150px;
    min-width: 0;
    padding: 16px;
    overflow: visible;
    background: #fff;
    border-radius: 8px;
  }

  &__card--small {
    flex: 1 0 0;
  }

  &__card--gas {
    flex-shrink: 0;
    width: 285px;
  }

  &__title,
  &__title-row h2 {
    margin: 0;
    color: #292a2e;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0;
    // white-space: nowrap;
    word-break: break-word;
  }

  &__title-row {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    height: 22px;
  }

  &__info {
    position: relative;
    display: flex;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;

    img {
      display: block;
      width: 16px;
      height: 16px;
    }
  }

  &__tooltip {
    position: absolute;
    bottom: 30px;
    left: 50%;
    z-index: 20;
    box-sizing: border-box;
    display: block;
    width: max-content;
    max-width: 240px;
    padding: 12px 16px;
    color: #292a2e;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    white-space: normal;
    word-break: break-word;
    pointer-events: none;
    visibility: hidden;
    background: #fff;
    border-radius: 8px;
    box-shadow:
      0 0 1px 0 rgba(30, 31, 33, 0.16),
      0 8px 24px 0 rgba(30, 31, 33, 0.16);
    opacity: 0;
    transform: translateX(-50%);

    &::after {
      position: absolute;
      bottom: -10px;
      left: 50%;
      display: block;
      width: 24px;
      height: 14px;
      content: '';
      background: #fff;
      filter: drop-shadow(0 6px 10px rgba(30, 31, 33, 0.1));
      transform: translateX(-50%);
      clip-path: path('M12 14C9 14 6 8 0 0L24 0C18 8 15 14 12 14Z');
    }
  }

  &__info:hover &__tooltip,
  &__info:focus-within &__tooltip {
    visibility: visible;
    opacity: 1;
  }

  &__greed-chart {
    position: relative;
    flex-shrink: 0;
    width: 100%;
    height: 74.4px;
    overflow: hidden;
  }

  &__gauge {
    position: absolute;
    top: 0;
    left: 50%;
    width: 148.8px;
    height: 148.8px;
    transform: translateX(-50%);
  }

  html[dir='rtl'] &__gauge {
    transform: translateX(-50%) scaleX(-1);
  }

  &__greed-info {
    position: absolute;
    bottom: 0;
    left: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    transform: translateX(-50%);
  }

  &__greed-value {
    margin: 0;
    color: #292a2e;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    text-align: center;
  }

  &__greed-label {
    margin: 0;
    color: #8c8f97;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    text-align: center;
  }

  &__season {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  &__season-labels {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    color: #505258;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  &__season-bars {
    display: block;
    width: 100%;
    height: 6px;
  }

  &__season-bar-wrap {
    width: 100%;
    height: 6px;
    overflow: hidden;
  }

  &__season-track {
    position: relative;
    width: 100%;
    padding-bottom: 8px;
  }

  html[dir='rtl'] &__season-track {
    transform: scaleX(-1);
  }

  &__season-indicator {
    position: absolute;
    top: 6px;
    left: 0;
    margin-top: 2px;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    transform: translateX(-50%);
    transition: opacity 0.2s ease;
    pointer-events: none;
    will-change: left, opacity;
  }

  &__season-value-wrap {
    display: flex;
    align-items: baseline;
    gap: 2px;
  }

  &__season-value {
    color: #292a2e;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  &__season-max {
    color: #8c8f97;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
  }

  &__dominance {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 84px;
  }

  &__dominance-chart {
    position: relative;
    display: block;
    flex-shrink: 0;
    width: 84px;
    height: 84px;
    border-radius: 50%;
  }

  &__dominance-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    width: 73px;
    height: 76px;
  }

  &__dominance-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 73px;
    height: 20px;
  }

  &__dominance-dot {
    display: block;
    flex-shrink: 0;
    width: 4px;
    height: 4px;
    border-radius: 50%;
  }

  &__dominance-value {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 20px;

    img {
      display: block;
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      object-fit: cover;
    }

    strong {
      flex-shrink: 0;
      color: #292a2e;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      white-space: nowrap;
    }
  }

  &__volmex {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    color: #292a2e;
    font-size: 13px;
    line-height: 20px;
    word-break: break-word;
  }

  &__volmex-row {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    height: 20px;

    span {
      flex: 1 0 0;
      min-width: 0;
      font-weight: 400;
    }

    strong {
      flex-shrink: 0;
      font-weight: 500;
      white-space: nowrap;
    }
  }

  &__gas-list {
    display: flex;
    flex: 1 0 0;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 0;
  }

  &__gas-item {
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    height: 84px;
    min-width: 0;
  }

  &__gas-label,
  &__gas-value,
  &__gas-time {
    width: 100%;
    margin: 0;
    letter-spacing: 0;
    word-break: break-word;
  }

  &__gas-label {
    color: #7d818a;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  &__gas-value {
    color: #292a2e;
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
  }

  &__gas-time {
    display: flex;
    justify-content: first;
    color: #7d818a;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
  }
}

@media screen and (max-width: 1200px) and (min-width: 801px) {
  .market-data-page {
    --market-data-container-width: clamp(800px, calc(100% - 48px), 1200px);
    padding-bottom: 80px;
  }

  .market-data-summary,
  .market-data-insights,
  .market-data-indicators {
    gap: 16px;
  }

  .market-data-insights {
    &__top-card {
      width: 220px;
    }

    &__chart,
    &__echart {
      width: 100%;
    }
  }

  .market-data-indicators {
    &__group {
      flex: 1 1 0;
      width: auto;
      min-width: 0;
      gap: 16px;
    }

    &__card--gas {
      width: 285px;
    }
  }
}

@media screen and (max-width: 1179px) {
  .market-data-insights {
    flex-direction: column;
    align-items: stretch;
    height: auto;

    &__chart-card {
      flex: none;
      width: 100%;
    }

    &__top-card {
      width: 100%;
      height: auto;
      min-height: 132px;
    }

    &__crypto-list {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 16px;
      margin-top: 16px;
    }

    &__crypto-item {
      min-width: 0;
    }

    &__sparkline {
      display: none;
    }
  }

  .market-data-indicators {
    flex-direction: column;
    height: auto;

    &__group,
    &__card--gas {
      width: 100%;
    }
  }
}

@media screen and (max-width: 943px) {
  .market-data-insights {
    &__chart {
      height: 237px;
    }
  }
}

@media screen and (max-width: 800px) {
  .market-data-page {
    --market-data-container-width: 800px;

    min-width: 800px;
  }
}

@keyframes market-data-trend-down {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(2px);
  }
}

@keyframes market-data-trend-up {
  0%,
  100% {
    transform: scaleY(-1) translateY(0);
  }

  50% {
    transform: scaleY(-1) translateY(2px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .market-data-summary__trend-icon,
  .market-data-insights__crypto-change img {
    animation: none;
  }

  .market-data-summary__trend-icon--up,
  .market-data-insights__crypto-trend-icon--up {
    transform: scaleY(-1);
  }
}
</style>
